import React, { useEffect } from "react";
import Layout from "../components/layout/Layout";
import {
  FiDollarSign, FiShield, FiLayers, FiClock,
  FiBookOpen, FiInfo, FiCheck, FiTrendingUp
} from "react-icons/fi";
import UtilityServiceInstance from "../services/UtilityService";
import { setPlans } from "../store/slices/UtilitySlice";
import { useDispatch, useSelector } from "react-redux";


export default function Premium(props) {
  const {
    productName = "Predixio",
    freeLimit = 100,
    freeMarkets = ["1X2 (Win/Draw/Lose)"],
  } = props;
  const plans = useSelector((state) => state.Utility.plans);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const response = await UtilityServiceInstance.getSubscriptionPlans();
        const responseData = response.data;
        const { success, result } = responseData;
        if (success === true) {
          dispatch(setPlans(result.data));
        }
      } catch (error) {

      }
    };

    fetchSubscriptionPlans();
  }, []);

  const color = {
    pageBg: "#f5f7fb",
    ink: "#0f172a",
    inkSub: "#475569",
    brand: "#2563eb",
    brandAlt: "#1d4ed8",
    border: "rgba(15, 23, 42, .10)",
    cardBg: "#ffffff",
    s2: "#ffffff",
    s3: "#f3f6ff",
    s4: "#ffffff",
    s5: "#fff7ed",
    s6: "#eefdf5",
  };

  const ui = {
    container: { maxWidth: 1400, margin: "0 auto", padding: "28px 16px", boxSizing: "border-box" },
    section: (bg) => ({ padding: "28px 16px", background: bg, overflowX: "clip" }),
    heroWrap: {
      position: "relative",
      padding: "80px 16px",
      background:
        "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
      color: "white",
      borderBottom: "1px solid rgba(255,255,255,.06)",
      overflowX: "clip",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12,
      color: "#93c5fd", background: "rgba(147,197,253,.10)", border: "1px solid rgba(147,197,253,.30)",
      padding: "6px 12px", borderRadius: 999,
    },
    heroTitle: { margin: "14px 0 10px", color: '#fff', fontSize: 36, lineHeight: 1.15, fontWeight: 900, letterSpacing: "-0.02em" },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },

    h2: { margin: "0 0 8px", fontSize: 24, fontWeight: 900, color: color.ink, wordBreak: "break-word" },
    sub: { margin: 0, fontSize: 14, color: color.inkSub, maxWidth: 820 },

    card: { background: color.cardBg, border: `1px solid ${color.border}`, borderRadius: 16, padding: 18, color: color.ink, overflow: "hidden", boxSizing: "border-box" },
    price: { fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em" },
    small: { fontSize: 12, color: color.inkSub },
    btnPrimary: {
      height: 42, padding: "0 16px", background: `linear-gradient(180deg, ${color.brand}, ${color.brandAlt})`,
      color: "white", border: "1px solid rgba(37,99,235,.6)", borderRadius: 10, fontWeight: 800, cursor: "pointer",
    },
    btnGhost: {
      height: 42, padding: "0 14px", background: "transparent", color: color.ink,
      border: `1px solid ${color.border}`, borderRadius: 10, fontWeight: 800, cursor: "pointer",
    },
    pill: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, fontSize: 12, background: "#f1f5f9", border: `1px solid ${color.border}` },
    toggle: { display: "inline-flex", gap: 8, background: "#f1f5f9", border: `1px solid ${color.border}`, borderRadius: 999, padding: 4, flexWrap: "wrap" },
    toggleBtn: (active) => ({ padding: "6px 10px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700, background: active ? color.brand : "transparent", color: active ? "#fff" : color.ink, border: `1px solid ${active ? "#1e40af" : "transparent"}` }),
  };

  const grids = {
    plans: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", alignItems: "stretch" },
    details: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", alignItems: "stretch" },
    faq: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" },
  };

  const [billing, setBilling] = React.useState("monthly");

  const priceTag = (p) => {
    const v = billing === "yearly" ? p.yearlyPriceText : p.monthlyPriceText;
    return (
      <div style={ui.price}>{v}</div>
    );
  };

  const Bullet = ({ children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <FiCheck style={{ color: "#059669", marginTop: 3 }} />
      <div style={{ color: color.inkSub }}>{children}</div>
    </div>
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Plans & Pricing">
      <style>{`
        @media (max-width: 640px) {
          .heroTitle { font-size: 28px !important; }
        }
        /* make sure long words never cause overflow */
        section, h1, h2, p, table { word-break: break-word; overflow-wrap: anywhere; }
      `}</style>

      <section style={ui.heroWrap} aria-label="Hero">
        <div style={ui.heroInner}>
          <span style={ui.badge}><FiShield /> Premium Plans</span>
          <h1 className="heroTitle" style={ui.heroTitle}>Flexible plans for every bettor</h1>
          <p style={ui.heroSub}>
            {productName} is free to try with <strong>only 1X2 predictions</strong> and up to <strong>{freeLimit} predictions per user</strong>. Unlock
            all markets, in-play signals, exports and API with Pro and Elite.
          </p>
        </div>
      </section>

      <section style={ui.section(color.s2)} aria-label="Overview">
        <div style={ui.container}>
          <h2 style={ui.h2}><FiDollarSign style={{ verticalAlign: "text-bottom" }} /> How our plans work</h2>
          <p style={ui.sub}>
            We keep it simple: the <strong>Free</strong> plan covers the essentials so you can test drive our predictions. If you want
            more markets (BTTS, Over/Under, Asian Handicap, Corners, Cards, Props), <strong>In-Play</strong> edges, exports and API, pick <strong>Pro</strong> or
            <strong> Elite</strong>. Switch billing anytime.
          </p>
        </div>
      </section>

      <section style={ui.section(color.s3)} aria-label="Plans">
        <div style={ui.container}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <div style={{ ...ui.pill }}><FiInfo /> Prices shown in USD; taxes may apply.</div>
            <div style={ui.toggle} role="tablist" aria-label="Billing period">
              <button style={ui.toggleBtn(billing === "monthly")} onClick={() => setBilling("monthly")} role="tab" aria-selected={billing === "monthly"}>Monthly</button>
              <button style={ui.toggleBtn(billing === "yearly")} onClick={() => setBilling("yearly")} role="tab" aria-selected={billing === "yearly"}>Yearly <span style={{ opacity: .8 }}>– 2 months free</span></button>
            </div>
          </div>
          <div style={grids.plans}>
            {plans.map((p) => (
              <div key={p.id} style={{ ...ui.card, display: "grid", gap: 12, height: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 900 }}>{p.name}</div>
                </div>
                <div style={{ color: color.inkSub }}>{p.description}</div>
                <div>{priceTag(p)}</div>
                <div style={{ fontWeight: 700, color: color.ink }}>{p.title}</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {p.features.map((b, i) => <Bullet key={i}>{b?.description}</Bullet>)}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                  <button style={ui.btnPrimary} onClick={() => alert(`${p.name} selected`)}> Choose plan </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={ui.section(color.s4)} aria-label="Details">
        <div style={ui.container}>
          <h2 style={ui.h2}><FiTrendingUp style={{ verticalAlign: "text-bottom" }} /> Why upgrade</h2>
          <p style={ui.sub}>Pro and Elite unlock everything beyond Free’s 1X2 predictions and single-user cap.</p>

          <div style={{ ...grids.details, marginTop: 12 }}>
            <div style={{ ...ui.card, display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <FiLayers /><div><strong>All markets</strong><div style={ui.small}>BTTS, Over/Under, Asian Handicap, Corners, Cards, Props.</div></div>
              </div>
            </div>
            <div style={{ ...ui.card, display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <FiClock /><div><strong>In-Play edges</strong><div style={ui.small}>Live momentum, pace, and pressure indicators.</div></div>
              </div>
            </div>
            <div style={{ ...ui.card, display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <FiBookOpen /><div><strong>Exports & API</strong><div style={ui.small}>CSV/JSON export and API for automation & analysis.</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={ui.section(color.s3)} aria-label="FAQ">
        <div style={ui.container}>
          <h2 style={ui.h2}><FiInfo style={{ verticalAlign: "text-bottom" }} /> Frequently asked</h2>
          <div style={{ ...grids.faq, marginTop: 12 }}>
            <div style={{ ...ui.card }}>
              <strong>What exactly is free?</strong>
              <p style={ui.sub}>Only {freeMarkets.join(", ")} and up to {freeLimit} predictions per user. No other markets, no in-play, no exports/API.</p>
            </div>
            <div style={{ ...ui.card }}>
              <strong>Can I switch plans later?</strong>
              <p style={ui.sub}>Yes, upgrade/downgrade anytime. Yearly plans pro-rate on renewal.</p>
            </div>
            <div style={{ ...ui.card }}>
              <strong>Do you offer team access?</strong>
              <p style={ui.sub}>Elite supports multi-seat and higher API limits. Contact us to tailor seats and limits.</p>
            </div>
            <div style={{ ...ui.card }}>
              <strong>Responsible use</strong>
              <p style={ui.sub}>Our tips are informational; bet responsibly. We never guarantee outcomes.</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
